import { useCallback, useMemo } from "react";
import { createRowClickHandler, type UnifiedItem } from "./documentTableSelection";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableRows } from "./useTableRows";
import type { useTableSelectionState } from "./useTableSelectionState";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  "selectedFolderIds" | "store" | "onFolderSelect" | "showCheckbox"
> &
  Pick<ReturnType<typeof useTableSelectionState>, "anchorIndex" | "setAnchorIndex"> &
  Pick<ReturnType<typeof useTableRows>, "unifiedItems">;
export function useTableClickActions(options: Options) {
  const {
    selectedFolderIds,
    store,
    onFolderSelect,
    showCheckbox,
    anchorIndex,
    unifiedItems,
    setAnchorIndex,
  } = options;
  // Helpers to check/set selection for a unified item (folder or document)
  const isItemSelected = useCallback(
    (item: UnifiedItem) => {
      if (item.type === "folder") return selectedFolderIds.includes(item.id);
      return store?.isSelected(item.id) ?? false;
    },
    [selectedFolderIds, store]
  );

  const setItemSelected = useCallback(
    (item: UnifiedItem, selected: boolean) => {
      if (item.type === "folder") {
        onFolderSelect?.(item.id, selected);
      } else {
        store?.selectDocument(item.id, selected);
      }
    },
    [store, onFolderSelect]
  );

  // Row click handler (capture phase) — delegates to createRowClickHandler so the
  // handler runs before Radix Checkbox's own onClick.
  const rowClickDeps = useMemo(
    () => ({
      showCheckbox: !!showCheckbox,
      hasStore: !!store,
      anchorIndex,
      unifiedItems,
      isItemSelected,
      setItemSelected,
      setAnchorIndex,
    }),
    [
      showCheckbox,
      store,
      anchorIndex,
      unifiedItems,
      isItemSelected,
      setItemSelected,
      setAnchorIndex,
    ]
  );

  const handleRowClickCapture = useCallback(
    (itemId: string, unifiedIndex: number) =>
      createRowClickHandler(rowClickDeps, itemId, unifiedIndex),
    [rowClickDeps]
  );
  return { handleRowClickCapture };
}
